import { yupResolver } from '@hookform/resolvers/yup';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { TranslationKey } from 'providers/i18n/types';
import { DeepPartial, useForm, UseFormReturn } from 'react-hook-form';
import * as yup from 'yup';

type UseGatewayRulesFormReturnValues = {
  form: UseFormReturn<GatewayRulesFormValues, any>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

type Props = {
  defaultValues?: DeepPartial<GatewayRulesFormValues>;
  onSubmit: (data: GatewayRulesFormValues) => void;
};

type TFunction = (key: TranslationKey, options?: Record<string, any>) => string;

const getGatewayRuleSchemas = (translate: TFunction) => {
  const expressionSchema = yup.object().shape({
    comparisonOperator: yup.lazy((value) =>
      typeof value === 'object'
        ? yup.object().required().typeError(translate('app.validation.required'))
        : yup.string().required(translate('app.validation.required')),
    ),
    expressionParam: yup.object().shape({
      id: yup.number(),
      name: yup.string(),
      alias: yup.string(),
      fieldType: yup.string(),
    }),
    value: yup
      .array()
      .of(yup.string().min(1, translate('app.validation.required')))
      .min(1, translate('app.validation.required')),
  });

  const conditionGroupSchema: any = yup.object({
    children: yup.array().of(yup.lazy(() => conditionGroupSchema.default(undefined))),
    expressions: yup.array().of(expressionSchema),
    isAnd: yup.boolean(),
  });

  const mappingItemSchema = yup.object({
    gateway: yup.string().required(translate('app.validation.required')),
    merchantAccount: yup.string().required(translate('app.validation.required')),
    commissions: yup
      .array(yup.string())
      .min(1, translate('app.validation.required'))
      .required(translate('app.validation.required')),
    value: yup
      .number()
      .required(translate('app.validation.required'))
      .min(0, translate('app.validation.number.noNegative')),
    subagent: yup.number().required(translate('app.validation.required')),
  });

  const mappingSchemaDirection = yup.object().shape({
    active: yup.boolean(),
    method: yup.string().required(translate('app.validation.required')),
    direction: yup.string().required(translate('app.validation.required')),
    sortOrder: yup
      .number()
      .notRequired()
      .moreThan(-1, translate('app.validation.number.noNegative'))
      .integer(translate('app.validation.number.integer')),
    description: yup
      .string()
      .max(1024, translate('app.validation.string.maxLength', { max: 1024 }))
      .notRequired(),
    mappingItems: yup.array().of(mappingItemSchema).required(),
  });

  const formSchemaDirection = yup.object().shape({
    name: yup
      .string()
      .required(translate('app.validation.required'))
      .max(255, translate('app.validation.string.maxLength', { max: 255 }))
      .min(3, translate('app.validation.string.minLength', { min: 3 })),
    merchant: yup.string().required(translate('app.validation.required')),
    active: yup.boolean().required(translate('app.validation.required')),
    conditionGroup: conditionGroupSchema.notRequired(),
    mappings: yup.array().of(mappingSchemaDirection).required(),
  });

  return {
    expressionSchema,
    conditionGroupSchema,
    mappingItemSchema,
    mappingSchemaDirection,
    formSchemaDirection,
  };
};

type Schemas = ReturnType<typeof getGatewayRuleSchemas>;

export type MappingFormValues = yup.InferType<Schemas['mappingSchemaDirection']>;
export type GatewayRulesFormValues = yup.InferType<Schemas['formSchemaDirection']>;
export type MappingItemFormValues = yup.InferType<Schemas['mappingItemSchema']>;

export function useGatewayRulesForm({
  defaultValues,
  onSubmit,
}: Props): UseGatewayRulesFormReturnValues {
  const translate = useTypedTranslate();

  const { formSchemaDirection } = getGatewayRuleSchemas(translate);

  const form = useForm<GatewayRulesFormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(formSchemaDirection),
    defaultValues,
  });

  function onSubmitHandler(data: GatewayRulesFormValues): void {
    onSubmit(data);
  }

  return {
    form,
    onSubmit: form.handleSubmit(onSubmitHandler),
  };
}
